import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { FileText } from 'lucide-react';
import { QualificationTemplate } from '../../lib/api';
import { ResponseMap } from './types';
import { QuestionInput } from './QuestionInput';

interface QuestionsTabProps {
  template: QualificationTemplate | null;
  responses: ResponseMap;
  qualificationStatus: string;
  isSupplier: boolean;
  onResponseChange: (questionId: number, value: string | number | boolean | string[], questionType: string) => void;
}

export function QuestionsTab({
  template,
  responses,
  qualificationStatus,
  isSupplier,
  onResponseChange,
}: QuestionsTabProps) {
  if (!template?.sections || template.sections.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No questions available</h3>
          <p className="text-muted-foreground">
            The template does not have any sections or questions configured.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {template.sections
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((section) => (
          <Card key={section.id}>
            <CardHeader>
              <CardTitle>{section.title}</CardTitle>
              {section.description && (
                <CardDescription>{section.description}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {section.questions
                ?.sort((a, b) => a.sort_order - b.sort_order)
                .map((question, idx) => (
                  <div key={question.id} className="space-y-2">
                    <div className="flex items-start justify-between">
                      <Label className="text-base font-medium">
                        {idx + 1}. {question.question_text}
                        {question.is_required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {question.is_scored && (
                        <Badge variant="outline" className="ml-2">
                          {responses[question.id]?.score_awarded ?? 0} / {question.max_score} pts
                        </Badge>
                      )}
                    </div>
                    {question.help_text && (
                      <p className="text-sm text-muted-foreground">{question.help_text}</p>
                    )}
                    <QuestionInput
                      question={question}
                      responses={responses}
                      qualificationStatus={qualificationStatus}
                      isSupplier={isSupplier}
                      onResponseChange={onResponseChange}
                    />
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
